<template>
  <div class="rounded-xl bg-neutral-100 p-4" ref="form">
    <!-- Error Messages -->
    <div
      class="bg-red-100 rounded border border-red-300 text-red-900 px-4 py-3 mt-4 mb-3"
      v-if="error"
      id="comment-error"
    >
      <p>{{ error }}</p>
    </div>
    <div ref="guestChallenge" class="mb-3"></div>
    <div
      class="bg-red-100 rounded border border-red-300 text-red-900 px-4 py-3 mt-4 mb-3 cursor-pointer"
      v-if="emailError"
    >
      <p>
        {{ $t('Email is already in use.') }}
        <a @click="clickLoginBtn()"> {{ $t('Click here') }} </a>
        {{ $t('to login and add your idea') }}
      </p>
    </div>

    <!-- Edit Comment Mode -->
    <div class="flex items-start" v-if="editComment" id="edit-comment-input">
      <div class="flex-1">
        <RichTextEditor
          ref="commentEditor"
          v-model="message"
          :input-id="`edit_comment_${commentData.id}`"
          input-name="comment[body]"
          variant="comment"
          :placeholder="$t('What do you think?')"
          :enable-mentions="true"
          :mention-users="discussionUsers || []"
          :enable-image-upload="false"
          :enable-embeds="false"
          :enable-drafts="false"
          @initialized="onEditorInitialized"
        />
      </div>
    </div>

    <!-- Authenticated User Mode -->
    <div class="flex items-start" v-else-if="currentUser" id="comment-input">
      <avatar
        v-if="this.screenWidth >= 500"
        class="avatar shadow-md mr-4 border-2 border-white flex-shrink-0"
        :class="{ 'comment__avatar--admin': currentProjectAdmin }"
        :src="currentUser.avatar"
        :username="currentUser.name"
        :size="36"
      ></avatar>

      <div class="flex-1">
        <RichTextEditor
          ref="commentEditor"
          v-model="commentContent"
          :input-id="`new_comment_${id}`"
          input-name="comment[body]"
          variant="comment"
          :placeholder="$t('What do you think?')"
          :enable-mentions="true"
          :mention-users="discussionUsers || []"
          :enable-image-upload="false"
          :enable-embeds="false"
          :enable-drafts="true"
          :draft-id="`comment_${postId}`"
          @initialized="onEditorInitialized"
        />
      </div>
    </div>

    <!-- Anonymous User Mode -->
    <div class="flex flex-col" id="comment-input" v-else>
      <RichTextEditor
        ref="commentEditor"
        v-model="commentContent"
        :input-id="`new_comment_anon_${id}`"
        input-name="comment[body]"
        variant="comment"
        :placeholder="$t('What do you think?')"
        :enable-mentions="true"
        :mention-users="discussionUsers || []"
        :enable-image-upload="false"
        :enable-embeds="false"
        :enable-drafts="true"
        :draft-id="`comment_anon_${postId}`"
        @initialized="onEditorInitialized"
      />

      <div
        class="flex flex-col md:flex-row gap-1 mt-2"
        v-if="this.projectSsoOnly !== 'true'"
      >
        <input
          type="text"
          v-model="name"
          name="name"
          id="name"
          class="input"
          :placeholder="$t('Name')"
        />
        <input
          v-model="email"
          name="email"
          id="email"
          class="input"
          :placeholder="$t('Email')"
        />
        <input
          type="hidden"
          class="remark"
          name="remark"
          pattern="[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4}$"
          placeholder="name@domain.com"
          v-model="remark"
        />
      </div>

      <label
        class="text-neutral-700 text-xs mt-3 flex items-start gap-2 md:mt-2 cursor-pointer"
        v-if="this.projectSsoOnly !== 'true'"
      >
        <input
          required="required"
          type="checkbox"
          v-model="agreed_with_privacy_policy"
          name="agree_checkbox"
          id="user_agree_checkbox"
          class="mt-0.5 flex-shrink-0"
        />
        <span>
          <span>{{ $t("I agree with") }}</span>
          <a
            href="https://www.feedbear.com/privacy-policy"
            :title="$t('Privacy Policy')"
            target="_blank"
            class="text-neutral-600 underline hover:no-underline hover:text-project-500"
            >{{ $t("Privacy Policy") }}</a
          >
          {{ $t("and") }}
          <a
            href="https://www.feedbear.com/terms"
            :title="$t('Terms of Service')"
            target="_blank"
            class="text-neutral-600 underline hover:no-underline hover:text-project-500"
            >{{ $t("Terms and Conditions") }}</a
          >
        </span>
      </label>
    </div>

    <!-- AI Suggestions Loading -->
    <div
      v-if="isLoading && !isError"
      class="flex flex-col mt-6 mb-8 justify-center items-center"
    >
      <Spinner border-color="#6b7280" />
      <p class="text-sm text-neutral-600 mt-8">
        {{ $t('Please hold on, we are creating some replies.') }}
      </p>
    </div>

    <!-- AI Suggestions -->
    <div
      id="suggestions"
      v-if="!isLoading && !isError"
      style="opacity: 1; transition: opacity 1s"
    >
      <p
        class="text-md ml-1 text-neutral-600 mt-4 mb-2"
        v-if="this.accept && this.reject && this.elaborate && this.describe"
      >
        {{ $t('Here are a few suggestions for your reply, click on the one that sounds more appropriate to edit before posting.') }}
      </p>
      <div class="mt-2 mb-2 flex flex-row" v-if="this.accept">
        <p class="text-2xl p-5 whitespace-nowrap">&#128077;</p>
        <p
          id="accept"
          class="p-3 bg-white text-neutral-600 text-md rounded-lg border-2 hover:bg-gray-200 transition ease-in-out duration-150 cursor-pointer changeFill"
          @click.prevent="select('accept')"
        >
          {{ this.accept }}
        </p>
      </div>
      <div class="mt-2 mb-2 flex flex-row" v-if="this.elaborate">
        <p class="text-2xl p-5 whitespace-nowrap">&#129300;</p>
        <p
          id="elaborate"
          class="p-3 bg-white text-neutral-600 text-md rounded-lg border-2 hover:bg-gray-200 transition ease-in-out duration-150 cursor-pointer changeFill"
          @click.prevent="select('elaborate')"
        >
          {{ this.elaborate }}
        </p>
      </div>
      <div class="mt-2 mb-2 flex flex-row" v-if="this.describe">
        <p class="text-2xl p-5 whitespace-nowrap">&#128591;</p>
        <p
          id="describe"
          class="p-3 bg-white text-neutral-600 text-md rounded-lg border-2 hover:bg-gray-200 transition ease-in-out duration-150 cursor-pointer changeFill"
          @click.prevent="select('describe')"
        >
          {{ this.describe }}
        </p>
      </div>
    </div>

    <!-- AI Error -->
    <transition name="fade" v-if="!isLoading && isError">
      <div class="alert alert--bad rounded-xl mt-4 mb-4">
        <p class="-ml-1">
          {{ $t('There was an error generating the text.') }}
          <a
            href="#"
            class="text-white underline"
            @click.prevent="suggestComment()"
            >{{ $t('Please try again...') }}</a
          >
        </p>
      </div>
    </transition>

    <!-- Action Buttons -->
    <transition name="popdown">
      <!-- Edit Mode Actions -->
      <div v-if="editComment">
        <div class="flex items-center flex-wrap mt-2" v-if="!uploadImage">
          <a
            :href="file.url"
            target="_blank"
            v-for="(file, index) in commentData.attached_files"
            :key="index"
            class="w-20 h-20 mr-2 shadow-lg border border-neutral-200 rounded-lg transform hover:scale-105 hover:shadow-xl transition duration-150 ease-in-out"
          >
            <img
              :src="file.preview_url"
              class="object-cover h-20 w-full rounded-lg"
              alt
            />
          </a>
        </div>
        <div class="comment_button items-start flex flex-col md:flex-row justify-between md:mt-4">
          <div>
            <FileUpload
              name="comment[attachments][]"
              class=""
              noun="images"
              :attach-file="commentData.attached_files"
              :upy-id="'uppyTrigger' + commentData.id"
              :editor-input-ids="[`edit_comment_${commentData.id}`]"
            >
            </FileUpload>
          </div>
          <div class="flex items-center">
            <a
              href="#"
              class="flex items-center text-neutral-700 mr-2 hover:no-underline text-sm hover:text-project-500 float-right"
              @click.prevent="cancelEditComment()"
              type="button"
            >
              {{ $t("Cancel") }}
            </a>
            <a
              href="#"
              class="flex items-center text-neutral-700 mr-2 hover:no-underline text-sm hover:text-project-500 float-right"
              @click.prevent="saveEditComment()"
              type="button"
            >
              {{ $t("Save") }}
            </a>
          </div>
        </div>
      </div>

      <!-- Normal Mode Actions -->
      <div
        v-else
        class="comment_button flex items-start justify-between mt-2 md:mt-4 gap-1"
        id="comment-file-upload"
      >
        <FileUpload
          name="comment[attachments][]"
          class="mb-3 md:mt-2 mr-4"
          :noun="$t('images')"
          :upy-id="id"
          :editor-input-ids="[`new_comment_${id}`, `new_comment_anon_${id}`]"
        >
        </FileUpload>
        <div class="flex items-start">
          <!-- AI Reply Button -->
          <button
            id="suggest-comment"
            class="btn mr-2 mb-2 xs:w-full justify-center items-justify"
            @click.prevent="suggestComment()"
            v-if="
              !accept &&
              !reject &&
              !elaborate &&
              !describe &&
              this.currentProjectAdmin &&
              this.commentsSuggestions == 'true'
            "
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 544 512"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              class="mr-2"
            >
              <path
                d="M234.7 42.7L192 64L234.7 85.3L256 128L277.3 85.3L320 64L277.3 42.7L256 0L234.7 42.7ZM384.4 192.4L351.6 159.6L432 79.2L464.8 112L384.4 192.4ZM96 32L64 96L0 128L64 160L96 224L128 160L192 128L128 96L96 32ZM416 352L352 384L416 416L448 480L480 416L544 384L480 352L448 288L416 352ZM144 512L183.6 472.4L504.4 151.6L544 112L504.4 72.4L471.6 39.6L432 0L392.4 39.6L71.6 360.4L32 400L71.6 439.6L104.4 472.4L144 512Z"
                fill="black"
              />
            </svg>
            {{ $t('Reply using AI') }}
          </button>

          <!-- SSO Login Button -->
          <button
            @click.prevent="ssoPopUp()"
            class="btn-main show_button_below"
            v-if="
              !currentUser &&
              this.projectSsoOnly === 'true' &&
              customWebsiteHref != ''
            "
            id="comment-btn"
            :disabled="isSubmitting"
          >
            <div class="flex items-center justify-center gap-2">
              <svg v-if="isSubmitting" class="spinner" stroke="currentColor" fill="none" stroke-width="2" viewBox="0 0 24 24" stroke-linecap="round" stroke-linejoin="round" height="20px" width="20px" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 6l0 -3"></path>
                <path d="M16.25 7.75l2.15 -2.15"></path>
                <path d="M18 12l3 0"></path>
                <path d="M16.25 16.25l2.15 2.15"></path>
                <path d="M12 18l0 3"></path>
                <path d="M7.75 16.25l-2.15 2.15"></path>
                <path d="M6 12l-3 0"></path>
                <path d="M7.75 7.75l-2.15 -2.15"></path>
              </svg>
              {{ isSubmitting ? $t('Submitting...') : $t('Comment') }}
            </div>
          </button>

          <!-- Submit Button -->
          <button
            @click="submitComment()"
            class="btn-main show_button_below"
            v-else
            id="comment-btn"
            :disabled="isSubmitting"
          >
            <div class="flex items-center justify-center gap-2">
              <svg v-if="isSubmitting" class="spinner" stroke="currentColor" fill="none" stroke-width="2" viewBox="0 0 24 24" stroke-linecap="round" stroke-linejoin="round" height="20px" width="20px" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 6l0 -3"></path>
                <path d="M16.25 7.75l2.15 -2.15"></path>
                <path d="M18 12l3 0"></path>
                <path d="M16.25 16.25l2.15 2.15"></path>
                <path d="M12 18l0 3"></path>
                <path d="M7.75 16.25l-2.15 2.15"></path>
                <path d="M6 12l-3 0"></path>
                <path d="M7.75 7.75l-2.15 -2.15"></path>
              </svg>
              {{ isSubmitting ? $t('Submitting...') : $t('Comment') }}
            </div>
          </button>
        </div>
      </div>
    </transition>

    <!-- SSO Login Dialog -->
    <Dialog v-model="ssoLoginPopup">
      <div class="px-4 pt-2 pb-4 sm:px-6">
        <div class="text-center font-semibold">
          {{ $t("Log in to proceed") }}
        </div>
        <div class="p-4 text-center">
          {{ $t("You must be logged in to submit feedback") }}, <br />{{
            $t("click the button below to login")
          }}
        </div>
        <div class="flex justify-center">
          <a
            :href="this.customWebsiteHref"
            @click="ssoAddComment()"
            class="text-white rounded-lg shadow-sm px-4 py-2 text-sm font-medium"
            style="background-color: var(--accent-color)"
          >
            {{ $t("Sign up") }} / {{ $t("Log in") }}
          </a>
        </div>
      </div>
    </Dialog>
  </div>
</template>

<script>
import Avatar from "vue-avatar";
import { guestCaptchaToken, renderGuestChallenge } from "./guest_captcha";
import { RichTextEditor } from "./components/rich_text_editor";
import FileUpload from "./components/file_upload";
import Dialog from "./components/dialog";
import EventBus from "event_bus";
import Spinner from "./projects/spinner_big";

export default {
  props: [
    "post-id",
    "comment-id",
    "reply-to-user",
    "current-user",
    "discussion-users",
    "parent-comment-id",
    "time-now",
    "edit-comment",
    "comment-data",
    "id",
    "added-new-comment",
    "project-sso-only",
    "custom-website-href",
    "board-slug",
    "current-project-admin",
    "show-ai-message",
    "subscription-valid",
    "comments-suggestions",
    "comments-base-url"
  ],

  data: function() {
    return {
      message: this.editComment ? this.commentData.body : "",
      commentContent: "",
      name: "",
      email: "",
      error: "",
      emailError: "",
      user_ip_address: "",
      remark: "",
      screenWidth: null,
      agreed_with_privacy_policy: false,
      uploadImage: false,
      ssoLoginPopup: false,
      editorInstance: null,
      accept: this.accept,
      reject: this.reject,
      elaborate: this.elaborate,
      describe: this.describe,
      isLoading: false,
      isError: false,
      isSubmitting: false,
      guestChallengeToken: ""
    };
  },

  components: {
    Avatar,
    RichTextEditor,
    FileUpload,
    Dialog,
    Spinner
  },

  mounted() {
    this.screenWidth = window.innerWidth;
    this.uploadedImageHandler = data => {
      this.uploadImage = data;
    };
    EventBus.$on("uploadedImage", this.uploadedImageHandler);

    if (this.addedNewComment) {
      window.scrollTo({
        top: document.body.scrollHeight,
        behavior: "smooth"
      });
    }

    // Restore draft from localStorage
    var commentData = JSON.parse(window.localStorage.getItem("comment"));
    if (commentData && commentData.post_id == this.postId) {
      this.commentContent = commentData.message;
      this.agreed_with_privacy_policy = commentData.agreed_with_privacy_policy;
      localStorage.removeItem("comment");
    }
  },

  computed: {
    // Base path for comment endpoints. Post pages pass board-slug/post-id as
    // before; changelog pages pass comments-base-url ("/updates/:id/comments").
    commentsBase() {
      return (
        this.commentsBaseUrl ||
        "/boards/" + this.boardSlug + "/posts/" + this.postId + "/comments"
      );
    }
  },

  methods: {
    onEditorInitialized(editor) {
      this.editorInstance = editor;
      if (this.replyToUser && this.replyToUser.handle) {
        const input = this.$refs.commentEditor;
        // Use the Trix API after initialization; direct DOM edits are not
        // reflected reliably in the editor document or submitted HTML.
        if (!input.getContent().trim()) {
          editor.insertString(`@${this.replyToUser.handle} `);
        }
        input.focus();
      }
    },

    getCommentBody() {
      // Get content from the editor
      if (this.$refs.commentEditor) {
        return this.$refs.commentEditor.getContent().trim();
      }
      return this.editComment ? this.message : this.commentContent;
    },

    getCommentHTML() {
      if (this.$refs.commentEditor) {
        // Trix syncs its serialized value asynchronously; if it hasn't caught
        // up with the live document yet, fall back to the v-model content so
        // we never submit an empty body while the editor visibly has text.
        return this.$refs.commentEditor.getHTML() || this.commentContent || '';
      }
      return '';
    },

    getMentions() {
      if (this.$refs.commentEditor) {
        const editor = this.$refs.commentEditor;
        const mentions = editor.getMentions();
        // Trix stores the automatic reply prefix as text, not a mention span.
        // Only submit it while it remains in the reply; deleting it opts out.
        if (this.replyToUser && this.replyToUser.handle) {
          const mention = `@${this.replyToUser.handle}`;
          const content = editor.getContent();
          if (content === mention || content.startsWith(`${mention} `)) {
            mentions.push(mention);
          }
        }
        return [...new Set(mentions)];
      }
      return [];
    },

    submitComment: async function() {
      if (this.remark != "") return;
      this.isSubmitting = true;
      this.error = "";

      var data = new FormData();
      var comment_post_url = "";
      var parent_id = this.commentId;

      const commentBody = this.getCommentBody();

      // Validate
      let error = commentBody === "" ? `${this.$t("Comment body is empty")}.` : "";

      if (!this.currentUser) {
        if (this.email) {
          error =
            error +
            (/^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/.test(
              this.email
            )
              ? ""
              : ` ${this.$t("Invalid email address")}`);
        }
        error = error + (this.name == "" ? ` ${this.$t("Name is empty")},` : "");
        error = error + (this.email == "" ? ` ${this.$t("Email is empty")},` : "");
        error =
          error +
          (!this.agreed_with_privacy_policy
            ? ` ${this.$t(
                "You'll need to accept our Privacy Policy and Terms to post a comment"
              )}`
            : "");
      }

      if (error) {
        this.error = error;
        this.isSubmitting = false;
        return;
      }

      // Get mentions
      const mentions = this.getMentions();

      // Build form data
      data.append("comment[body]", this.getCommentHTML());
      data.append("mention_users", mentions);
      data.append("comment[name]", this.name);
      data.append("comment[email]", this.email);
      data.append("comment[agreed_with_privacy_policy]", this.timeNow);
      if (!this.currentUser) {
        data.append("captcha_token", await guestCaptchaToken("GUEST_COMMENT"));
        if (this.guestChallengeToken) {
          data.append("captcha_challenge_token", this.guestChallengeToken);
          this.guestChallengeToken = "";
        }
      }

      // Get attachments
      let attachments = Array.from(
        this.$refs.form.querySelectorAll("input[name='comment[attachments][]']"),
        el => el.value
      );

      attachments.forEach(file => {
        data.append("comment[attachments][]", file);
      });

      if (this.parentCommentId !== 0) {
        parent_id = this.parentCommentId;
      }

      if (parent_id) {
        comment_post_url = this.commentsBase + "/" + parent_id + "/comments";
      } else {
        comment_post_url = this.commentsBase;
      }

      Rails.ajax({
        url: comment_post_url,
        type: "POST",
        data: data,
        dataType: "json",
        beforeSend: function() {
          return true;
        },
        success: data => {
          this.emailError = data.email_used;
          if (!this.emailError) {
            if (parent_id) {
              data.parent_comment_id = parent_id;
              this.$emit("newNestedComment", data);
              EventBus.$emit("newNestedComment", data);
            } else {
              this.$emit("newComment", data);
            }

            // Clear the editor
            if (this.$refs.commentEditor) {
              this.$refs.commentEditor.clearContent();
            }
            this.commentContent = "";
            this.name = "";
            this.email = "";
            this.error = "";
            if (this.$refs.guestChallenge) this.$refs.guestChallenge.innerHTML = "";
            EventBus.$emit("imagesUpload", true);
          }

          // Fade out suggestions
          const suggestionsEl = document.getElementById("suggestions");
          if (suggestionsEl) {
            suggestionsEl.style.opacity = "0";
            setTimeout(() => suggestionsEl.remove(), 700);
          }
          this.isSubmitting = false;
        },
        error: (data, status) => {
          this.emailError = data && data.email_used;
          if (data && data.challenge_required) {
            this.error = data.error;
            renderGuestChallenge(this.$refs.guestChallenge, "GUEST_COMMENT").then(token => {
              this.guestChallengeToken = token;
              this.submitComment();
            });
          } else if (!this.emailError) {
            if (typeof data === "string" && data.length) {
              this.error = data;
            } else if (data && data.error) {
              this.error = data.error;
            } else {
              this.error = `${this.$t("Your comment could not be posted. Please try again")}.`;
            }
          }
          this.isSubmitting = false;
        }
      });
    },

    clickLoginBtn: function() {
      const login = document.getElementById("auth-login-btn");
      if (login) {
        login.click();
      }
    },

    saveEditComment: function() {
      const commentBody = this.editComment ? this.message : this.getCommentBody();

      let error = commentBody == "" ? "Comment body is empty." : "";
      if (error) {
        this.error = error;
        return;
      }

      var data = new FormData();
      let attachments = Array.from(
        this.$refs.form.querySelectorAll("input[name='comment[attachments][]']"),
        el => el.value
      );

      attachments.forEach(file => {
        data.append("comment[attachments][]", file);
      });

      data.append("comment[body]", this.editComment ? this.message : this.getCommentHTML());

      var pinUrl = this.commentsBase + "/" + this.commentData.id;

      Rails.ajax({
        url: pinUrl,
        type: "PUT",
        data: data,
        dataType: "json",
        processData: true,
        beforeSend: function() {
          return true;
        },
        success: data => {
          this.$emit("updateCommentData", data);
          this.error = "";
        },
        error: (data, status) => {
          this.error = data;
          console.log(data);
        }
      });
    },

    cancelEditComment: function() {
      this.$emit("editHide", false);
    },

    ssoPopUp() {
      this.ssoLoginPopup = true;
    },

    ssoAddComment() {
      const comment_data = {
        message: this.commentContent,
        agreed_with_privacy_policy: this.agreed_with_privacy_policy,
        post_id: this.postId
      };
      window.localStorage.setItem("comment", JSON.stringify(comment_data));
      this.ssoLoginPopup = false;
    },

    suggestComment() {
      this.isLoading = true;
      this.isError = false;
      Rails.ajax({
        url: `/boards/${this.boardSlug}/posts/${this.postId}/comments/suggest`,
        type: "GET",
        dataType: "json",
        beforeSend: function() {
          return true;
        },
        success: data => {
          this.isLoading = false;
          this.isError = false;
          this.accept = data["positive_reply"];
          this.reject = data["thankful_but_non_priority_reply"];
          this.elaborate = data["reply_requesting_more_information"];
          this.describe = data["reply_explaining_the_process"];
        },
        error: (data, status) => {
          console.log(data);
          this.isLoading = false;
          this.isError = true;
        }
      });
    },

    closeAIMessage() {
      Rails.ajax({
        url: `/comments/remove_ai_message`,
        type: "POST",
        dataType: "json",
        beforeSend: function() {
          return true;
        },
        success: data => {
          const aiMessage = document.getElementById("ai-message");
          if (aiMessage) {
            aiMessage.remove();
          }
        },
        error: (data, status) => {
          console.log(data);
        }
      });
    },

    select(type) {
      const selectedText =
        type === "accept"
          ? this.accept
          : type === "elaborate"
          ? this.elaborate
          : this.describe;

      // Insert the selected text into the editor
      if (this.$refs.commentEditor) {
        this.$refs.commentEditor.clearContent();
        this.$refs.commentEditor.insertText(selectedText);
      }
      this.commentContent = selectedText;

      // Update visual selection state
      ["accept", "elaborate", "describe"].forEach(t => {
        const el = document.getElementById(t);
        if (el) {
          if (t === type) {
            el.classList.remove("bg-white");
            el.classList.add("bg-black", "bg-opacity-5");
          } else {
            el.classList.remove("bg-black", "bg-opacity-5");
            el.classList.add("bg-white");
          }
        }
      });
    }
  },

  beforeDestroy() {
    EventBus.$off("uploadedImage", this.uploadedImageHandler);
  }
};
</script>

<style scoped>
.offset {
  margin-left: calc(36px + 1rem);
}

@media only screen and (max-width: 500px) and (min-width: 320px) {
  #suggest-comment {
    width: 100%;
    justify-content: center;
    margin-bottom: 0.5rem;
  }
  .show_button_below {
    width: 100%;
    text-align: center !important;
    display: block;
  }
  .comment_button {
    display: block;
  }
}

.spinner {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>

<style>
.mention {
  color: var(--accent-color, #009be5) !important;
  background-color: color-mix(in srgb, var(--accent-color, #009be5) 10%, transparent) !important;
  padding: 0 4px;
}
</style>
